// ── Step 4: Compliance Check & Algorithm Tips Module ─────────────
// Owns: step-compliance panel DOM, compliance & algorithm API calls.

import {
  getState,
  setState,
  subscribe,
} from './state.js';
import { ALGORITHM_KEYS, PRIORITY_COLORS } from './constants.js';
import { collectCurrentListing } from './listing-utils.js';
import { diagnosticFacade } from './interaction/facades/diagnostic.facade.instance.js';
import {
  compactBusinessMetaForRevision,
  buildListingRevisionTextHash,
  buildRevisionCompletionScopeKey,
  compactKeywordLibraryForRevision,
  compactProductEvidenceForRevision,
  compactRevisionSuggestionContext,
  normalizeRevisionAdviceText,
  renderListingRevisionText,
  isRevisionCompletionCurrent,
} from './listing-draft-payload.js';

// ── DOM Helpers ─────────────────────────────────────────────────

const $ = (sel) => document.querySelector(sel);

function createStep4Element(tagName, className = '', text = undefined) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined && text !== null) element.textContent = String(text);
  return element;
}

function replaceStep4Children(selector, ...children) {
  const host = $(selector);
  if (host) host.replaceChildren(...children);
  return host;
}

function normalizeDisplayText(value) {
  return value === undefined || value === null ? '' : String(value);
}

function createComplianceEmptyState(message, tone = '') {
  return createStep4Element('div', `empty-state${tone ? ` ${tone}` : ''}`, message);
}

export function formatRevisionReviewFailure(error) {
  const raw = normalizeRevisionAdviceText(error);
  if (/\binsufficient[_\s-]?balance\b|余额\s*不足|余额不足|MiniMax API Error\s*\(402\)/i.test(raw)) {
    return '模型服务余额不足，请充值或切换可用模型后重新诊断';
  }
  return raw || '模型没有返回可解析的审稿结论';
}

function getComplianceScoreTone(score, unavailable) {
  if (unavailable) return 'is-unavailable';
  const numericScore = Number(score);
  if (numericScore >= 80) return 'is-safe';
  if (numericScore >= 50) return 'is-warning';
  return 'is-danger';
}

function createComplianceScoreCard({ score, title, description, tone, showScoreSuffix = true }) {
  const card = createStep4Element('div', 'compliance-score-card');
  const scoreValue = createStep4Element('div', `compliance-score-value ${tone}`, score);
  if (showScoreSuffix) scoreValue.append(createStep4Element('small', 'compliance-score-suffix', '/100'));
  const copy = createStep4Element('div', 'compliance-score-copy');
  copy.append(
    createStep4Element('div', 'compliance-score-title', title),
    createStep4Element('div', 'compliance-score-description', description),
  );
  card.append(scoreValue, copy);
  return card;
}

function getSeverityTone(severity) {
  return ['critical', 'warning', 'info'].includes(severity) ? severity : 'info';
}

function createViolationCard(violation) {
  const card = createStep4Element('div', 'violation-card');
  const meta = createStep4Element('div', 'violation-card-meta');
  const severity = getSeverityTone(violation?.severity);
  meta.append(
    createStep4Element('span', `violation-severity violation-severity--${severity}`, normalizeDisplayText(violation?.severity)),
    createStep4Element('span', 'violation-field', normalizeDisplayText(violation?.field)),
  );
  const term = createStep4Element('div', 'violation-term');
  term.append(createStep4Element('strong', '', normalizeDisplayText(violation?.term)));
  card.append(
    meta,
    term,
    createStep4Element('div', 'violation-suggestion', normalizeDisplayText(violation?.suggestion)),
  );
  return card;
}

function createAlgorithmTipGroup(label, items) {
  const group = createStep4Element('section', 'algorithm-tip-group');
  group.append(createStep4Element('h4', 'algorithm-tip-title', label));
  const tipItems = Array.isArray(items) ? items : [];
  group.append(...tipItems.map((tip) => {
    const priority = Object.hasOwn(PRIORITY_COLORS, tip?.priority) ? tip.priority : 'low';
    const item = createStep4Element('div', 'algorithm-tip-item');
    item.append(
      createStep4Element('span', `algorithm-tip-priority algorithm-tip-priority--${priority}`, `[${normalizeDisplayText(tip?.priority)}]`),
      document.createTextNode(normalizeDisplayText(tip?.message)),
    );
    return item;
  }));
  return group;
}

export function createDiagnosticSingleFlight() {
  let activeKey = '';
  let activePromise = null;

  async function execute(key, task) {
    const normalizedKey = String(key || '');
    if (activePromise) {
      if (activeKey === normalizedKey) return activePromise;
      try {
        await activePromise;
      } catch {
        // The caller waiting for a newer draft still gets its own diagnostic.
      }
      return execute(normalizedKey, task);
    }

    activeKey = normalizedKey;
    const promise = Promise.resolve().then(task);
    activePromise = promise;
    try {
      return await promise;
    } finally {
      if (activePromise === promise) {
        activePromise = null;
        activeKey = '';
      }
    }
  }

  return execute;
}

const runDiagnosticSingleFlight = createDiagnosticSingleFlight();

export function getOptimizationCenterKey(listing) {
  return buildListingRevisionTextHash(listing || {});
}

function mapRevisionSuggestions(raw = []) {
  return (Array.isArray(raw) ? raw : []).map((s, idx) => ({
    id: `rev-${idx}`,
    field: s.field || '通用',
    dimension: s.dimension || '',
    severity: s.severity || 'info',
    ruleCode: s.ruleCode || s.type || '',
    level: s.level || '',
    businessImpact: s.businessImpact || '',
    evidenceRequired: s.evidenceRequired === true,
    blocksWriteback: s.blocksWriteback === true,
    revisionAction: s.revisionAction || '',
    issue: s.issue || s.original || '',
    original: s.original || '',
    suggestion: s.suggestion || s.suggestedFix || '',
    editedSuggestion: s.editedSuggestion || s.suggestion || s.suggestedFix || '',
    evidence: s.evidence || null,
    offendingTerms: Array.isArray(s.offendingTerms)
      ? [...s.offendingTerms]
      : (Array.isArray(s.evidence?.offendingTerms) ? [...s.evidence.offendingTerms] : []),
    items: Array.isArray(s.items) ? s.items : [],
    impactSummary: s.impactSummary || '',
    itemCount: Number(s.itemCount) || (Array.isArray(s.items) ? s.items.length : 1),
    reasonSummary: s.reasonSummary || '',
    evidenceSummary: Array.isArray(s.evidenceSummary) ? s.evidenceSummary : [],
  }));
}

function buildVisibleDiagnosticSuggestions({ complianceResult = null, scoringReport = null } = {}) {
  const compliance = (complianceResult?.violations || []).map((item) => ({
    ...item,
    field: item.field || '通用',
    issue: item.issue || item.message || (item.term ? `存在合规风险：${item.term}` : '存在合规风险'),
    suggestion: item.suggestion || item.recommendedAction || '删除或改写该风险内容。',
    original: item.original || item.term || '',
  }));
  const scoring = (scoringReport?.suggestions || []).map((item) => ({
    ...item,
    field: item.field || item.dimension || '通用',
    issue: item.issue || item.message || item.title || '评分检测发现可优化项',
    suggestion: item.suggestion || item.recommendedAction || item.description || item.message || '',
  }));
  return [...compliance, ...scoring].filter((item) => item.issue || item.suggestion);
}

function buildUnavailableComplianceResult() {
  return {
    score: null,
    violations: [],
    availability: 'unavailable',
  };
}

function buildRevisionBaselineQuality(scoringReport = null) {
  const scoreValue = scoringReport?.overallScore
    ?? scoringReport?.score
    ?? scoringReport?.semanticReview?.overallScore
    ?? scoringReport?.semanticReview?.score;
  const overallScore = Number.isFinite(Number(scoreValue)) ? Number(scoreValue) : null;
  return {
    overallScore,
    protectionTier: overallScore !== null && overallScore >= 85 ? 'high_score' : 'standard',
  };
}

async function populateScoringAndRevision(listing, { forceFreshReview = false } = {}) {
  const currentKey = getOptimizationCenterKey(listing);
  setState('revisionSuggestionsError', '');
  setState('revisionAdviceText', '');
  // 每次重新诊断先收回执行权，避免旧草稿的建议卡在新请求期间解锁改稿。
  setState('revisionSuggestionsMeta', {
    basedOnDraftKey: currentKey,
    marketplace: listing.marketplace || getState('marketplace') || 'US',
    generatedAt: new Date().toISOString(),
    baselineQuality: buildRevisionBaselineQuality(getState('scoringReport')),
    executionAuthority: {
      actionable: false,
      conclusion: 'not_evaluated',
      status: 'diagnosing',
      fullTextReviewFailed: false,
    },
    copyQuality: null,
  });

  // Step04 has one audit owner. Do not pre-fill its output with checklist
  // scores or word-rule findings that can turn ordinary copy into a false
  // blocker before the Listing expert has read the complete draft.
  let scoringReport = null;
  setState('scoringReport', null);

  try {
    const scoringSource = scoringReport || getState('scoringReport');

    const listingGenerationMeta = getState('listingGenerationMeta');
    const productInfo = getState('productInfo') || listingGenerationMeta?.mergeMeta?.businessMeta?.productInfo || null;
    const listingText = renderListingRevisionText(listing);
    const listingTextHash = buildListingRevisionTextHash(listingText);
    // Step04 owns the only semantic audit. A Step03 snapshot is never a
    // substitute for this call, including older saved drafts that still carry
    // a legacy draftSelfReview field.
    const revisionData = await diagnosticFacade.suggestRevision(compactRevisionSuggestionContext({
      jobId: getState('batchJobId') || '',
      complianceResult: getState('complianceResult'),
      scoringReport: scoringSource || { suggestions: [] },
      currentListing: listing,
      marketplace: listing.marketplace || getState('marketplace') || 'US',
      keywordLibrary: compactKeywordLibraryForRevision(getState('keywordLibrary')),
      productEvidence: compactProductEvidenceForRevision({
        productInfo,
        sourceSnapshots: getState('batchSnapshots') || getState('snapshots') || [],
      }),
      productInfo,
      draftSelfReview: null,
      forceFreshReview: true,
      listingText,
      listingTextHash,
      businessMeta: compactBusinessMetaForRevision({
        productInfo,
        keywordUsePolicies: getState('listingGenerationMeta')?.mergeMeta?.businessMeta?.keywordUsePolicies || [],
        competitorRoles: getState('listingGenerationMeta')?.mergeMeta?.businessMeta?.competitorRoles || [],
        referenceFactContract: getState('listingGenerationMeta')?.mergeMeta?.businessMeta?.referenceFactContract || null,
      }),
    }));

    if (revisionData.scoringReport && typeof revisionData.scoringReport === 'object') {
      scoringReport = revisionData.scoringReport;
      setState('scoringReport', scoringReport);
    }

    setState('revisionOverview', revisionData.overview || null);
    const suggestionsDraftKey = currentKey;
    const remainingSuggestions = Array.isArray(revisionData.suggestions)
      ? revisionData.suggestions
      : [];
    // 执行文本只能来自后端全文审稿结果。诊断卡只用于展示，
    // 不得在客户端重新拼成一份可执行的改稿指令。
    const resolvedAdviceText = normalizeRevisionAdviceText(revisionData.revisionAdviceText);
    const suggestionReview = revisionData.suggestionReview || {};
    const reviewFailureMessage = suggestionReview.fullTextReviewFailed === true
      ? `完整 Listing 审稿未完成：${formatRevisionReviewFailure(suggestionReview.error)}。本轮仅保留确定性检查项，未执行全文优化。`
      : '';
    persistStep04QualityConclusion({
      fullTextAudit: revisionData.fullTextAudit,
      suggestionReview,
      listingTextHash,
    });
    setState('revisionSuggestions', mapRevisionSuggestions(remainingSuggestions));
    setState('revisionAdviceText', resolvedAdviceText);
    setState('revisionSuggestionsError', reviewFailureMessage);
    setState('revisionSuggestionsMeta', {
      basedOnDraftKey: suggestionsDraftKey,
      marketplace: listing.marketplace || getState('marketplace') || 'US',
      listingTextHash,
      generatedAt: new Date().toISOString(),
      baselineQuality: buildRevisionBaselineQuality(scoringReport || getState('scoringReport')),
      executionAuthority: {
        actionable: revisionData.actionable === true,
        conclusion: String(suggestionReview.conclusion || '').trim().toLowerCase(),
        status: String(suggestionReview.status || '').trim().toLowerCase(),
        fullTextReviewFailed: suggestionReview.fullTextReviewFailed === true,
      },
      copyQuality: suggestionReview.quality || revisionData.fullTextAudit?.quality || null,
    });
  } catch (err) {
    console.warn('[step4] Unable to populate revision suggestions', err);
    const fallbackSuggestions = buildVisibleDiagnosticSuggestions({
      complianceResult: getState('complianceResult'),
      scoringReport: scoringReport || getState('scoringReport'),
    });
    setState('revisionSuggestions', mapRevisionSuggestions(fallbackSuggestions));
    setState('revisionAdviceText', '');
    setState('revisionOverview', null);
    setState('revisionSuggestionsMeta', {
      basedOnDraftKey: currentKey,
      marketplace: listing.marketplace || getState('marketplace') || 'US',
      generatedAt: new Date().toISOString(),
      baselineQuality: buildRevisionBaselineQuality(scoringReport || getState('scoringReport')),
      executionAuthority: {
        actionable: false,
        conclusion: 'not_evaluated',
        status: 'request_failed',
        fullTextReviewFailed: true,
      },
      copyQuality: null,
    });
    setState(
      'revisionSuggestionsError',
      fallbackSuggestions.length
        ? '本轮已保留可用的检查项；当前 Listing 不会被覆盖。'
        : '本轮未生成新的改稿建议，当前 Listing 已保留。',
    );
  }
}

export async function diagnoseListingDraft(listing, { forceFreshReview = false } = {}) {
  const diagnosticMarketplace = listing.marketplace || getState('marketplace') || 'US';
  setState('marketplace', diagnosticMarketplace);
  setState('diagnosticMarketplace', diagnosticMarketplace);
  setState('complianceResult', buildUnavailableComplianceResult());
  setState('algorithmTips', null);
  await populateScoringAndRevision(listing, { forceFreshReview });
  return {
    complianceResult: getState('complianceResult'),
    scoringReport: getState('scoringReport'),
    revisionSuggestions: getState('revisionSuggestions') || [],
    revisionAdviceText: getState('revisionAdviceText') || '',
  };
}

// ── Compliance Result Render ────────────────────────────────────

function renderComplianceResult() {
  const result = getState('complianceResult');
  if (!result) return;

  $('#compliance-result').hidden = false;
  const foldoutSummary = document.getElementById('raw-diagnostics-summary');

  if (getState('diagnosisInFlight')) {
    replaceStep4Children('#compliance-score-card', createComplianceScoreCard({
      score: '待检测',
      title: '规则合规检查',
      description: '正在完成本轮诊断，结果生成后统一显示。',
      tone: 'is-unavailable',
      showScoreSuffix: false,
    }));
    if (foldoutSummary) foldoutSummary.textContent = '（待检测）';
    replaceStep4Children('#compliance-violations', createComplianceEmptyState('正在检测规则合规风险…'));
    return;
  }

  const unavailable = result.availability === 'unavailable';
  const score = unavailable ? '—' : (result.score || 0);
  const violations = Array.isArray(result.violations) ? result.violations : [];
  replaceStep4Children('#compliance-score-card', createComplianceScoreCard({
    score,
    title: '规则合规检查',
    description: unavailable
      ? '本轮未产生需要处理的规则项，当前 Listing 已保留。'
      : `${violations.length} 个风险；该分项不等于综合质量分`,
    tone: getComplianceScoreTone(score, unavailable),
  }));
  if (foldoutSummary) {
    foldoutSummary.textContent = `（${violations.length} 项）`;
  }
  if (violations.length === 0) {
    replaceStep4Children('#compliance-violations', createComplianceEmptyState(
      unavailable ? '本轮检查已完成，当前 Listing 已保留。' : '未发现合规问题',
      unavailable ? '' : 'is-compliance-success',
    ));
    return;
  }
  replaceStep4Children('#compliance-violations', ...violations.map(createViolationCard));
}

// ── Algorithm Tips Render ────────────────────────────────────────

function renderAlgorithmTips(tips) {
  if (!tips) return;
  $('#algorithm-tips').hidden = false;

  const groups = [];
  for (const [key, label] of Object.entries(ALGORITHM_KEYS)) {
    const items = Array.isArray(tips[key]) ? tips[key] : [];
    if (items.length === 0) continue;
    groups.push(createAlgorithmTipGroup(label, items));
  }

  replaceStep4Children(
    '#algorithm-tips-content',
    ...(groups.length ? groups : [createComplianceEmptyState('暂无优化建议')]),
  );
}

// ── Run Compliance Check ─────────────────────────────────────────

export function hasCurrentRevisionCompletion({
  listing = {},
  listingGenerationMeta = null,
  marketplace = '',
  productInfo = null,
  keywordUsePolicies = undefined,
  competitorRoles = undefined,
  businessContext = null,
} = {}) {
  const resolvedProductInfo = productInfo || listingGenerationMeta?.mergeMeta?.businessMeta?.productInfo || null;
  const resolvedPolicies = keywordUsePolicies === undefined
    ? (businessContext?.keywordUsePolicies || [])
    : keywordUsePolicies;
  const resolvedRoles = competitorRoles === undefined
    ? (businessContext?.competitorRoles || [])
    : competitorRoles;
  const scopeKey = buildRevisionCompletionScopeKey({
    listing,
    marketplace: marketplace || resolvedProductInfo?.marketplace || resolvedProductInfo?.targetMarket || '',
    productInfo: resolvedProductInfo,
    keywordUsePolicies: resolvedPolicies,
    competitorRoles: resolvedRoles,
  });
  return isRevisionCompletionCurrent(listingGenerationMeta?.mergeMeta?.revisionCompletion, scopeKey);
}

function getRevisionCompletionContext(listing) {
  const listingGenerationMeta = getState('listingGenerationMeta');
  const productInfo = getState('productInfo') || null;
  const businessContext = getState('businessContext') || null;
  return {
    listing,
    listingGenerationMeta,
    marketplace: getState('marketplace') || '',
    productInfo,
    keywordUsePolicies: getState('keywordUsePolicies') || businessContext?.keywordUsePolicies || [],
    competitorRoles: getState('competitorRoles') || businessContext?.competitorRoles || [],
    businessContext,
  };
}

/**
 * Decide the Step04 entry action before any diagnostic side effects begin.
 * A confirmed revision is terminal only for its exact Step03 draft and
 * business scope. This makes re-entry deliberately cheap while keeping the
 * explicit "重新诊断" action authoritative.
 */
export function getOptimizationCenterEntryDecision(context = {}) {
  return hasCurrentRevisionCompletion(context)
    ? { shouldDiagnose: false, status: 'revision_completion_reused' }
    : { shouldDiagnose: true, status: 'diagnosis_required' };
}

function supersedeRevisionCompletion() {
  const meta = getState('listingGenerationMeta');
  const completion = meta?.mergeMeta?.revisionCompletion;
  if (completion?.status !== 'approved') return;
  setState('listingGenerationMeta', {
    ...meta,
    mergeMeta: {
      ...meta.mergeMeta,
      revisionCompletion: {
        ...completion,
        status: 'superseded',
        supersededAt: new Date().toISOString(),
      },
    },
  });
}

function persistStep04QualityConclusion({ fullTextAudit = null, suggestionReview = {}, listingTextHash = '' } = {}) {
  const audit = fullTextAudit && typeof fullTextAudit === 'object' ? fullTextAudit : null;
  const conclusion = String(audit?.conclusion || suggestionReview?.conclusion || '').trim().toLowerCase();
  if (!['pass', 'needs_revision', 'facts_required'].includes(conclusion)) return;

  const meta = getState('listingGenerationMeta') || {};
  const previousReadiness = meta.readiness || {};
  const structurallyReady = previousReadiness.canProceedToFullTextReview !== false;
  const copyQuality = suggestionReview?.quality || audit?.quality || null;
  const quality = {
    status: conclusion,
    label: conclusion === 'pass'
      ? '质量复核通过'
      : conclusion === 'facts_required'
        ? '文案已生成，待补充事实'
        : '文案已生成，待质量复核',
    fullTextReviewRequired: false,
    canProceedToCompliance: conclusion === 'pass',
    canProceedToPublish: conclusion === 'pass',
    listingTextHash,
    reviewedAt: new Date().toISOString(),
    copyQuality,
  };
  setState('listingGenerationMeta', {
    ...meta,
    readiness: {
      ...previousReadiness,
      canProceedToFullTextReview: structurallyReady,
      canProceedToCompliance: structurallyReady && quality.canProceedToCompliance,
      canProceedToPublish: structurallyReady && quality.canProceedToPublish,
    },
    quality,
    mergeMeta: {
      ...(meta.mergeMeta || {}),
      audit: audit || {
        status: conclusion,
        conclusion,
        score: copyQuality?.score ?? suggestionReview?.score ?? null,
        quality: copyQuality,
      },
      finalQualityConclusion: quality,
    },
  });
}

export async function runComplianceCheck({ forceFreshReview = true } = {}) {
  const button = $('#run-diagnostic-button');
  const listing = collectCurrentListing({ full: true });
  if (!listing.titleEn && !listing.bulletsEn?.length) {
    $('#compliance-result').hidden = false;
    replaceStep4Children('#compliance-score-card', createComplianceEmptyState('请先生成 Listing 文案'));
    return;
  }
  // The top "重新诊断" control is intentionally the highest-priority path:
  // it never reuses a completed revision, even when every input still matches.
  if (forceFreshReview) supersedeRevisionCompletion();
  const diagnosticKey = getOptimizationCenterKey(listing);

  return runDiagnosticSingleFlight(diagnosticKey, async () => {
    // A new diagnosis always starts from the canonical Step03 draft. Pending
    // Search Terms edits are not committed until the revision writeback succeeds.
    setState('revisionSearchTermsDraft', null);

    // Advance navigation to step 4
    const highest = getState('highestStep');
    setState('highestStep', Math.max(highest, 4));
    setState('currentStep', 4);
    setState('diagnosisInFlight', true);
    const progressBanner = $('#diagnostic-progress-banner');
    if (progressBanner) {
      progressBanner.hidden = false;
      progressBanner.setAttribute('aria-busy', 'true');
      // 用户可能停留在页面下方，主动把优化中心顶部的进度提示滚动进视野。
      progressBanner.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    if (button) {
      button.disabled = true;
      button.textContent = '诊断中...';
    }

    try {
      const result = await diagnoseListingDraft(listing, { forceFreshReview });
      if (result && getState('algorithmTips')) renderAlgorithmTips(getState('algorithmTips'));
    } catch (err) {
      console.warn('[step4] Unexpected diagnostic orchestration failure; preserving current Listing.', err);
      $('#compliance-result').hidden = false;
      replaceStep4Children('#compliance-score-card', createComplianceEmptyState('本轮检查已完成，当前 Listing 已保留。'));
    } finally {
      setState('diagnosisInFlight', false);
      if (progressBanner) {
        progressBanner.hidden = true;
        progressBanner.setAttribute('aria-busy', 'false');
      }
      if (button) {
        button.disabled = false;
        button.textContent = '重新诊断';
      }
    }
  });
}

// ── Step3 确定性检查顺延复用 ────────────────────────────────────
// Step4 顺延复用 Step3 已前移的 mergeMeta.deterministicChecks（合规/评分/算法，
// 零 LLM）。mergeMeta.deterministicChecks 恒为最新结果；仅当结果 stale 时视为
// 不可复用。recompute.needsReview 时提示正文已变更、需要重审。

function readStep3DeterministicReuse() {
  const meta = getState('listingGenerationMeta');
  const mergeMeta = meta?.mergeMeta || {};
  const checks = mergeMeta.deterministicChecks;
  if (!checks || typeof checks !== 'object') return null;
  const recompute = mergeMeta.recompute || {};
  return {
    checks,
    action: String(recompute.action || 'noop'),
    needsReview: recompute.needsReview === true,
    stale: recompute.stale === true,
  };
}

/**
 * 用 Step3 前移的确定性检查结果填充 Step4 的合规/评分/算法面板，并标记"复用自 Step3"。
 * @returns {boolean} 是否成功复用（无可复用结果或已 stale 时返回 false）
 */
export function reuseStep3DeterministicChecks() {
  const reuse = readStep3DeterministicReuse();
  if (!reuse || reuse.stale) return false;
  const { checks } = reuse;
  const ok = (branch) => branch && branch.status === 'ok' && branch.data && typeof branch.data === 'object';
  const complianceData = ok(checks.compliance) ? checks.compliance.data : null;
  const scoringData = ok(checks.scoring) ? checks.scoring.data : null;
  const algorithmData = ok(checks.algorithm) ? checks.algorithm.data : null;
  if (!complianceData && !scoringData && !algorithmData) return false;

  if (complianceData) setState('complianceResult', complianceData);
  if (scoringData) setState('scoringReport', scoringData);
  if (algorithmData) setState('algorithmTips', algorithmData);

  setState('deterministicReuse', {
    reused: true,
    status: String(checks.status || 'ok'),
    needsReview: reuse.needsReview,
    reusedAt: new Date().toISOString(),
  });
  return true;
}

function renderDeterministicReuseNotice() {
  const notice = $('#deterministic-reuse-notice');
  if (!notice) return;
  const reuse = getState('deterministicReuse');
  if (!reuse || !reuse.reused) {
    notice.hidden = true;
    notice.textContent = '';
    return;
  }
  const statusLabel = { ok: '已通过', partial: '部分通过', error: '未通过' }[reuse.status] || String(reuse.status || 'ok');
  const reviewHint = reuse.needsReview ? '；正文已变更，需重审' : '';
  notice.textContent = `🧪 规则 / 评分 / 算法结果复用自 Step3（${statusLabel}）${reviewHint}`;
  notice.hidden = false;
}

// Navigation is read-only by default. When the user explicitly enters the
// optimization center, Step04 runs its own full-text audit; it never reuses a
// Step03 self-review.
// Snapshot/saved-listing restore and back navigation only change currentStep,
// so they never spend LLM calls in the background.
export function openOptimizationCenterForDiagnosis() {
  const highest = getState('highestStep');
  setState('highestStep', Math.max(highest, 4));
  setState('currentStep', 4);
  // 顺延复用 Step3 已前移的确定性检查（合规/评分/算法），不再强制手动点"运行检测/打分"。
  reuseStep3DeterministicChecks();
  renderDeterministicReuseNotice();
  const listing = collectCurrentListing({ full: true });
  const entry = getOptimizationCenterEntryDecision(getRevisionCompletionContext(listing));
  if (!entry.shouldDiagnose) {
    return Promise.resolve({ status: entry.status });
  }
  return runComplianceCheck({ forceFreshReview: true });
}

// ── Module Init ─────────────────────────────────────────────────

export function initStep4() {
  // 显式"重新诊断"是一次全新运行，不再标记为复用自 Step3。
  $('#run-diagnostic-button')?.addEventListener('click', () => {
    setState('deterministicReuse', null);
    renderDeterministicReuseNotice();
    return runComplianceCheck({ forceFreshReview: true });
  });

  // ── Prev Step Button ─────────────────────────────────────────────
  $('#step4-prev-button')?.addEventListener('click', () => setState('currentStep', 3));

  subscribe('state:complianceResultChanged', () => {
    renderComplianceResult();
  });
  subscribe('state:diagnosisInFlightChanged', () => {
    renderComplianceResult();
  });
  subscribe('state:deterministicReuseChanged', () => {
    renderDeterministicReuseNotice();
  });

  const existingCompliance = getState('complianceResult');
  if (existingCompliance) renderComplianceResult();
  const existingTips = getState('algorithmTips');
  if (existingTips) renderAlgorithmTips(existingTips);
  renderDeterministicReuseNotice();
}
